import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import api from "../services/api";
import CustomerImportHistoryFilters from "../components/customerImportHistory/CustomerImportHistoryFilters";
import CustomerImportHistoryTable from "../components/customerImportHistory/CustomerImportHistoryTable";

function toQuery(filters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.set(key, String(value));
    }
  });
  return params.toString();
}

export default function CustomerImportHistoryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({
    page: 1,
    limit: 25,
    sourceType: "",
    status: "",
    createdBy: "",
    dateFrom: "",
    dateTo: "",
    search: "",
  });

  const loadData = useCallback(async (nextFilters = filters) => {
    try {
      setLoading(true);
      setError("");
      const query = toQuery(nextFilters);
      const res = await api.get(`/clients/import/batches${query ? `?${query}` : ""}`);
      setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
      setPagination(res?.data?.pagination || { page: 1, limit: 25, total: 0, totalPages: 1 });
    } catch (err) {
      setError(err?.response?.data?.error || "Importhistorie laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData(filters);
  }, [filters, loadData]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const submitFilters = (event) => {
    event.preventDefault();
    loadData(filters);
  };

  const goToPage = (page) => {
    const next = { ...filters, page };
    setFilters(next);
    loadData(next);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4 md:p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-slate-900 p-2 text-white"><Building2 size={18} /></div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Logboek24</p>
                <h1 className="text-2xl font-semibold text-slate-900">Importhistorie</h1>
              </div>
            </div>
            <button type="button" onClick={() => navigate("/klanten/import")} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">Terug naar import</button>
          </div>
        </header>

        <CustomerImportHistoryFilters filters={filters} onChange={updateFilter} onSubmit={submitFilters} loading={loading} />

        {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

        <CustomerImportHistoryTable
          items={items}
          loading={loading}
          onOpen={(item) => navigate(`/klanten/import/historie/${item._id}`)}
        />

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => goToPage(Math.max(1, Number(pagination.page || 1) - 1))}
            disabled={Number(pagination.page || 1) <= 1 || loading}
            className="rounded border border-slate-200 px-3 py-1.5 text-xs text-slate-700 disabled:opacity-50"
          >
            Vorige
          </button>
          <span className="text-xs text-slate-600">Pagina {pagination.page} / {pagination.totalPages}</span>
          <button
            type="button"
            onClick={() => goToPage(Math.min(Number(pagination.totalPages || 1), Number(pagination.page || 1) + 1))}
            disabled={Number(pagination.page || 1) >= Number(pagination.totalPages || 1) || loading}
            className="rounded border border-slate-200 px-3 py-1.5 text-xs text-slate-700 disabled:opacity-50"
          >
            Volgende
          </button>
        </div>
      </div>
    </div>
  );
}
